import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatTimestamp } from '../utils/timestampUtils';
import './BoundaryDragHandle.css';

const BoundaryDragHandle = ({
  prevWord,
  nextWord,
  prevGlobalIndex,
  nextGlobalIndex,
  onBoundaryChange
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [previewTime, setPreviewTime] = useState(null);
  const handleRef = useRef(null);
  const startXRef = useRef(0);
  const startBoundaryRef = useRef(0);

  const currentBoundary = prevWord?.end || 0;
  const minBoundary = (prevWord?.start || 0) + 0.01;
  const maxBoundary = (nextWord?.end || 0) - 0.01;

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    startXRef.current = e.clientX;
    startBoundaryRef.current = currentBoundary;
    setPreviewTime(currentBoundary);

    const rect = handleRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPosition({ x: rect.left, y: rect.top - 30 });
    }
  }, [currentBoundary]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startXRef.current;
      // Convert pixels to seconds (roughly 50 pixels per second)
      const deltaSeconds = deltaX / 50;
      let newBoundary = startBoundaryRef.current + deltaSeconds;

      // Clamp to valid range
      newBoundary = Math.max(minBoundary, Math.min(maxBoundary, newBoundary));
      setPreviewTime(newBoundary);

      // Update tooltip position
      const rect = handleRef.current?.getBoundingClientRect();
      if (rect) {
        setTooltipPosition({ x: e.clientX - 30, y: rect.top - 30 });
      }
    };

    const handleMouseUp = (e) => {
      const deltaX = e.clientX - startXRef.current;
      const deltaSeconds = deltaX / 50;
      let newBoundary = startBoundaryRef.current + deltaSeconds;
      newBoundary = Math.max(minBoundary, Math.min(maxBoundary, newBoundary));

      if (Math.abs(newBoundary - startBoundaryRef.current) > 0.001) {
        onBoundaryChange(prevGlobalIndex, nextGlobalIndex, newBoundary);
      }

      setIsDragging(false);
      setPreviewTime(null);
      setTooltipPosition(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, minBoundary, maxBoundary, onBoundaryChange, prevGlobalIndex, nextGlobalIndex]);

  return (
    <>
      <span
        ref={handleRef}
        className={`boundary-handle ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleMouseDown}
        title={`Boundary at ${formatTimestamp(currentBoundary)}`}
      />
      {isDragging && tooltipPosition && createPortal(
        <div
          className="boundary-tooltip"
          style={{ left: tooltipPosition.x, top: tooltipPosition.y }}
        >
          {formatTimestamp(previewTime)}
        </div>,
        document.body
      )}
    </>
  );
};

export default BoundaryDragHandle;
